import { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { rpx } from 'client/lib/rpx-client';
import { useTryAsyncData } from 'client/lib/hooks';
import { useAsyncState } from 'client/lib/hooks/use-async-data';
import { DefaultSpinner } from '@components/spinner';
import { IcoCheck, IcoDownload, IcoX } from '@components/icons';
import { URLS } from 'shared/urls';
import { useRouteParams } from '@components/router';
import { urls } from './urls';
import { summariseRow } from 'shared/checkin-day-state';
import {
  latestDate,
  rosterStatus,
  RosterStatus,
  ROSTER_STATUS_LABELS,
} from 'shared/checkin-roster';
import { needsReply } from 'shared/checkin-feedback';
import { latestSummaryWindow } from 'shared/checkin-weeks';
import { CheckinRepliesView } from './checkin-replies-view';
import { CoachPicker, unassignedLabel } from '@course/components/checkins/coach-picker';
import { BtnPrimary } from '@components/buttons';
import { showError } from '@components/app-error';
import { fmtTrackerDate, trackerCountsLine } from 'shared/tracker-people';
import { TotalsView, TrackerChips, TrackerCountCell, trackerListColumns } from './tracker-coach';
import { participantChannels } from 'shared/tracker-config';
import { togglePillClass } from '@course/components/toggle-pill';

type Props = {
  courseId: UUID;
  baseUrl: string;
};

type View = 'replies' | 'list' | 'grid' | 'totals';
type StatusFilter = 'all' | RosterStatus;

const STATUS_ORDER: RosterStatus[] = ['quiet', 'away', 'posting', 'notDue'];

const STATUS_CLASSES: Record<RosterStatus, string> = {
  posting: 'bg-green-50 text-green-700',
  quiet: 'bg-red-50 text-red-700',
  away: 'bg-gray-100 text-gray-600',
  notDue: 'bg-gray-100 text-gray-600',
};

function HeadCell({
  children,
  class: className = '',
}: {
  class?: string;
  children: ComponentChildren;
}) {
  return <div class={`table-cell p-4 py-2 align-bottom ${className}`}>{children}</div>;
}

function BodyCell({
  children,
  class: className = '',
}: {
  class?: string;
  children: ComponentChildren;
}) {
  return <span class={`table-cell p-4 border-t align-top ${className}`}>{children}</span>;
}

function ToggleButton({
  isSelected,
  onClick,
  children,
}: {
  isSelected: boolean;
  onClick(): void;
  children: ComponentChildren;
}) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      class={togglePillClass(isSelected)}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function shortDate(date?: string | Date) {
  return date ? dayjs(date).format('ddd M/D') : '—';
}

export function CheckinsTab({ courseId, baseUrl }: Props) {
  const params = useRouteParams();
  const loader = useTryAsyncData(() => rpx.checkins.getCourseCheckinGrid({ courseId }), [courseId]);
  const [queue, setQueue] = useAsyncState(async () => {
    try {
      return await rpx.checkins.getCheckinQueue({ courseId });
    } catch {
      return 'failed' as const;
    }
  }, [courseId]);
  // The tracker's numbers; `{ enabled: false }` while it is off. A failure leaves them out.
  const [trackerOverview] = useAsyncState(async () => {
    try {
      return await rpx.tracker.getCourseTrackerOverview({ courseId });
    } catch {
      return { enabled: false as const };
    }
  }, [courseId]);
  // Coach changes made on this screen, by student, until the next full load.
  const [coachChanges, setCoachChanges] = useState<Record<UUID, UUID | ''>>({});
  const [selected, setSelected] = useState<Set<UUID>>(new Set());
  const [bulkCoach, setBulkCoach] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  function refreshQueue() {
    rpx.checkins
      .getCheckinQueue({ courseId })
      .then(setQueue)
      .catch(() => {
        // Keep showing what we have; the next full load catches up.
      });
  }

  // Reload quietly when a student's page closes: replies posted there must
  // clear the queue here, or a second coach answers the same standup. Quietly
  // means no spinner, so the coach keeps their filter and place.
  const openStudent = useRef(params.userId);
  useEffect(() => {
    const wasOpen = openStudent.current;
    openStudent.current = params.userId;
    if (!wasOpen || params.userId) {
      return;
    }
    refreshQueue();
    // The student's page can change their coach; reload the grid so the
    // List view's Coach column shows it.
    rpx.checkins
      .getCourseCheckinGrid({ courseId })
      .then((fresh) => {
        setReloadedGrid(fresh);
        setCoachChanges({});
      })
      .catch(() => {
        // Keep showing what we have.
      });
  }, [params.userId]);
  const [reloadedGrid, setReloadedGrid] =
    useState<Awaited<ReturnType<typeof rpx.checkins.getCourseCheckinGrid>>>();
  const [view, setView] = useState<View>('replies');
  const [filter, setFilterState] = useState<StatusFilter>('all');
  const grid = reloadedGrid ?? loader.data;

  // A selection only ever covers the students on screen.
  function setFilter(next: StatusFilter) {
    setFilterState(next);
    setSelected(new Set());
  }
  const queueItems = queue && queue !== 'failed' ? queue.items : [];
  const tracker = trackerOverview?.enabled ? trackerOverview : undefined;
  const trackerLive = tracker && !tracker.ended ? tracker : undefined;
  const trackerStudents = new Map(trackerLive?.students.map((s) => [s.userId, s]));
  const trackerColumns = trackerLive ? trackerListColumns(trackerLive.config) : [];
  const trackerLines = new Map(
    trackerLive?.students.map((s) => [s.userId, trackerCountsLine(trackerLive.config, s.now)]),
  );
  const needCount = queueItems.filter(needsReply).length;

  if (!grid) {
    if (loader.isLoading) {
      return <DefaultSpinner />;
    }
    return (
      <div class="bg-white rounded-sm border p-6 text-sm text-gray-600">
        We couldn't load the standups. Refresh the page to try again.
      </div>
    );
  }

  if (!grid.enabled) {
    return (
      <div class="bg-white rounded-sm border p-6 flex flex-col gap-2">
        <strong class="text-gray-800">Standups are off for this course.</strong>
        <span class="text-sm text-gray-600">
          Turn them on and write your prompts in{' '}
          <a
            class="text-indigo-600 underline"
            href={URLS.guide.coursePage({ courseId, page: 'settings/checkins' })}
          >
            standup settings
          </a>
          . Once they're on, this tab shows who has posted a standup and who hasn't.
        </span>
      </div>
    );
  }

  if (grid.students.length === 0) {
    return (
      <div class="bg-white rounded-sm border p-6 text-sm text-gray-600">
        No students are enrolled in this course yet.
      </div>
    );
  }

  const now = new Date();
  const rows = grid.students.map((s) => {
    const summary = summariseRow({
      days: grid.days,
      answeredDays: s.dates,
      startedOn: s.startedOn,
      today: s.localToday,
      startDate: grid.startDate,
      endDate: grid.endDate,
    });
    return {
      student: s,
      summary,
      status: rosterStatus({
        ...s,
        dueDays: grid.startDate ? summary.dueDays : undefined,
        now,
      }),
    };
  });
  // Newest day first, so this week sits next to the names and older weeks
  // are the ones you scroll to. `i` keeps each column tied to its cell.
  const gridColumns = grid.days.map((day, i) => ({ day, i })).reverse();
  const postedCount = rows.filter((r) => r.summary.answered > 0).length;
  // This week's summary, or last week's until the next one opens.
  const summaryWeek = grid.summariesEnabled
    ? latestSummaryWindow(grid.localToday, grid)
    : undefined;
  const summaryLabel = summaryWeek?.isSprint
    ? 'Sprint summary'
    : `Weekly summary (week ${summaryWeek?.weekNumber})`;
  const summaryByStudent = new Map(
    queueItems
      .filter((item) => item.kind === 'summary' && item.date === summaryWeek?.weekStart)
      .map((item) => [item.userId, item]),
  );
  const summariesSent = rows.filter((r) => summaryByStudent.has(r.student.userId)).length;

  function summaryCell(userId: UUID) {
    const sent = summaryByStudent.get(userId);
    if (sent) {
      return (
        <span
          class={`inline-block text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
            sent.isLate ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-700'
          }`}
        >
          {sent.isLate ? 'Sent late' : `Sent ${dayjs(sent.submittedAt).format('ddd')}`}
        </span>
      );
    }
    const dueToday = summaryWeek?.dueOn === grid?.localToday;
    const overdue = !!summaryWeek?.dueOn && grid!.localToday > summaryWeek.dueOn;
    return (
      <span
        class={`inline-block text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
          dueToday || overdue ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'
        }`}
      >
        {dueToday ? 'Not sent · due tonight' : 'Not sent'}
      </span>
    );
  }

  const summaryStrip = summaryWeek && (
    <div class="flex flex-wrap items-center justify-between gap-2 bg-white border rounded-sm px-4 py-3 text-sm">
      <span>
        <strong class="text-gray-800">
          {summaryWeek.isSprint
            ? 'Sprint summaries'
            : `Weekly summaries for week ${summaryWeek.weekNumber}`}
          : {summariesSent} of {rows.length} sent
        </strong>
        <span class="text-gray-600">
          {summaryWeek.dueOn
            ? ` · due by the end of ${dayjs(summaryWeek.dueOn).format('dddd, MMM D')}`
            : ' · due before each coaching call'}
        </span>
      </span>
      {summariesSent < rows.length && (
        <button type="button" class="text-indigo-600 underline" onClick={() => setView('list')}>
          See who hasn’t sent
        </button>
      )}
    </div>
  );
  const countFor = (status: RosterStatus) => rows.filter((r) => r.status === status).length;
  const listRows = rows
    .filter((r) => filter === 'all' || r.status === filter)
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));

  function assignedCoach(student: { userId: UUID; assignedCoachId?: UUID }) {
    const change = coachChanges[student.userId];
    return change === undefined ? student.assignedCoachId : change || undefined;
  }
  // Coaching is in use once there is more than one coach to choose from, a
  // default coach, or anyone assigned by hand.
  const coachingOn =
    grid.coaches.length > 1 ||
    !!grid.defaultCoachId ||
    rows.some((r) => !!assignedCoach(r.student));

  async function assignSelected() {
    setIsAssigning(true);
    try {
      const userIds = [...selected];
      // The endpoint takes up to 500 students per call.
      for (let i = 0; i < userIds.length; i += 500) {
        await rpx.checkins.setStudentCoach({
          courseId,
          userIds: userIds.slice(i, i + 500),
          coachUserId: (bulkCoach || undefined) as UUID | undefined,
        });
      }
      setCoachChanges((changes) => {
        const next = { ...changes };
        for (const id of userIds) {
          next[id] = (bulkCoach as UUID) || '';
        }
        return next;
      });
      setSelected(new Set());
      refreshQueue();
    } catch (err) {
      showError(err as Error);
    } finally {
      setIsAssigning(false);
    }
  }

  function toggleSelected(userId: UUID) {
    setSelected((ids) => {
      const next = new Set(ids);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }

  function waitingFor(userId: UUID) {
    return queueItems.filter((item) => item.userId === userId && needsReply(item)).length;
  }

  function studentLink(userId: UUID) {
    return urls.showStudent({ ...params, courseId, userId, tab: 'checkins', baseUrl });
  }

  function studentCell({ name, email, userId }: { name: string; email: string; userId: UUID }) {
    return (
      <BodyCell class="sticky left-0 bg-white">
        <a class="flex flex-col" href={studentLink(userId)}>
          <strong class="text-indigo-600">{name}</strong>
          <span class="text-gray-500 truncate">{email}</span>
        </a>
      </BodyCell>
    );
  }

  return (
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <ToggleButton isSelected={view === 'replies'} onClick={() => setView('replies')}>
            Replies
            {needCount > 0 && (
              <span class="ml-1.5 inline-block min-w-5 px-1.5 rounded-full bg-red-600 text-white text-xs">
                {needCount}
              </span>
            )}
          </ToggleButton>
          <ToggleButton isSelected={view === 'list'} onClick={() => setView('list')}>
            List
          </ToggleButton>
          <ToggleButton isSelected={view === 'grid'} onClick={() => setView('grid')}>
            Grid
          </ToggleButton>
          {tracker && (
            <ToggleButton isSelected={view === 'totals'} onClick={() => setView('totals')}>
              Totals
            </ToggleButton>
          )}
        </div>
        <a
          class="inline-flex items-center gap-1 text-sm text-gray-700 border rounded-md px-3 py-1.5 bg-white hover:bg-gray-50"
          download="standups.csv"
          href={`/csv/courses/${courseId}/standups.csv`}
        >
          <IcoDownload class="w-4 h-4" />
          Download standups (CSV)
        </a>
      </div>

      {grid.days.length === 0 && grid.startDate && (
        <div class="bg-white rounded-sm border p-6 text-sm text-gray-600">
          Standups start on {dayjs(grid.startDate).format('dddd, MMM D')}.
        </div>
      )}

      {view === 'replies' && !queue && <DefaultSpinner />}
      {view === 'replies' && queue === 'failed' && (
        <div class="bg-white rounded-sm border p-6 text-sm text-gray-600">
          We couldn't load the replies. Refresh the page to try again.
        </div>
      )}
      {view === 'replies' && queue && queue !== 'failed' && (
        <CheckinRepliesView
          courseId={courseId}
          items={queue.items}
          viewerId={queue.viewerId}
          queueDays={queue.queueDays}
          replyEmailsEnabled={queue.replyEmailsEnabled}
          header={summaryStrip}
          coaches={coachingOn ? grid.coaches : []}
          startDate={grid.startDate}
          studentHref={studentLink}
          trackerLine={(userId) => trackerLines.get(userId)}
          onFeedback={(itemId, feedback) =>
            setQueue((current) =>
              current && current !== 'failed'
                ? {
                    ...current,
                    items: current.items.map((item) =>
                      item.id === itemId ? { ...item, feedback } : item,
                    ),
                  }
                : current,
            )
          }
        />
      )}

      {view === 'list' && (
        <>
          <div class="flex flex-col gap-2">
            <strong class="text-gray-800">
              {postedCount} of {rows.length} students have posted a standup
              {summaryWeek &&
                ` · ${summariesSent} of ${rows.length} sent their ${
                  summaryWeek.isSprint
                    ? 'Sprint summary'
                    : `weekly summary for week ${summaryWeek.weekNumber}`
                }`}
              {trackerLive &&
                ` · ${rows.filter((r) => trackerStudents.get(r.student.userId)?.now.touches).length} of ${rows.length} logging this week`}
            </strong>
            <div class="flex flex-wrap items-center gap-2">
              <ToggleButton isSelected={filter === 'all'} onClick={() => setFilter('all')}>
                All · {rows.length}
              </ToggleButton>
              {STATUS_ORDER.map((status) => (
                <ToggleButton
                  key={status}
                  isSelected={filter === status}
                  onClick={() => setFilter(status)}
                >
                  {ROSTER_STATUS_LABELS[status]} · {countFor(status)}
                </ToggleButton>
              ))}
            </div>
            <span class="text-sm text-gray-600">Status covers the last 7 days.</span>
            {tracker?.ended && tracker.lastDay && (
              <span class="text-sm text-gray-500">
                Tracker numbers ended {fmtTrackerDate(tracker.lastDay, tracker.today)}.{' '}
                <button
                  type="button"
                  class="text-indigo-600 underline"
                  onClick={() => setView('totals')}
                >
                  Totals
                </button>{' '}
                stay.
              </span>
            )}
          </div>
          {coachingOn && selected.size > 0 && (
            <div class="flex flex-wrap items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-sm px-4 py-2 text-sm">
              <strong class="text-gray-800">{selected.size} selected</strong>
              <label class="flex items-center gap-2">
                Assign to
                <select
                  class="ruz-input py-1 text-sm w-auto"
                  value={bulkCoach}
                  onChange={(e) => setBulkCoach((e.target as HTMLSelectElement).value)}
                >
                  <option value="">{unassignedLabel(grid.coaches, grid.defaultCoachId)}</option>
                  {grid.coaches.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <BtnPrimary onClick={assignSelected} isLoading={isAssigning}>
                Assign coach
              </BtnPrimary>
              <button
                type="button"
                class="text-indigo-600 underline"
                onClick={() => setSelected(new Set())}
              >
                Clear selection
              </button>
            </div>
          )}
          <div class="overflow-x-auto">
            <div class="table bg-white rounded-sm border w-full divide-y">
              <div class="table-row bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {coachingOn && (
                  <HeadCell>
                    <input
                      type="checkbox"
                      aria-label="Select all students shown"
                      checked={
                        listRows.length > 0 && listRows.every((r) => selected.has(r.student.userId))
                      }
                      onChange={(e) =>
                        setSelected(
                          (e.target as HTMLInputElement).checked
                            ? new Set(listRows.map((r) => r.student.userId))
                            : new Set(),
                        )
                      }
                    />
                  </HeadCell>
                )}
                <HeadCell class="w-64">Student</HeadCell>
                {coachingOn && <HeadCell>Coach</HeadCell>}
                <HeadCell class="text-center">Posted</HeadCell>
                <HeadCell>Last standup</HeadCell>
                <HeadCell>Last in a lesson</HeadCell>
                {summaryWeek && <HeadCell>{summaryLabel}</HeadCell>}
                <HeadCell class="text-center">Needs a reply</HeadCell>
                <HeadCell>Status</HeadCell>
                {trackerColumns.map((column) => (
                  <HeadCell key={column.key} class="text-center">
                    <span title={column.title}>{column.label}</span>
                  </HeadCell>
                ))}
              </div>
              {listRows.map(({ student: s, summary, status }) => (
                <div key={s.userId} class="table-row text-sm">
                  {coachingOn && (
                    <BodyCell>
                      <input
                        type="checkbox"
                        aria-label={`Select ${s.name}`}
                        checked={selected.has(s.userId)}
                        onChange={() => toggleSelected(s.userId)}
                      />
                    </BodyCell>
                  )}
                  {studentCell(s)}
                  {coachingOn && (
                    <BodyCell>
                      <CoachPicker
                        courseId={courseId}
                        userId={s.userId}
                        studentName={s.name}
                        coaches={grid.coaches}
                        defaultCoachId={grid.defaultCoachId}
                        assignedCoachId={assignedCoach(s)}
                        onChange={(coachId) => {
                          setCoachChanges((changes) => ({ ...changes, [s.userId]: coachId || '' }));
                          refreshQueue();
                        }}
                      />
                    </BodyCell>
                  )}
                  <BodyCell
                    class={`text-center font-semibold whitespace-nowrap ${
                      summary.answered === 0 && summary.due > 0 ? 'text-red-700' : 'text-gray-800'
                    }`}
                  >
                    {summary.answered} of {summary.due}
                  </BodyCell>
                  <BodyCell class="whitespace-nowrap">{shortDate(latestDate(s.dates))}</BodyCell>
                  <BodyCell class="whitespace-nowrap">{shortDate(s.lastLessonVisitAt)}</BodyCell>
                  {summaryWeek && <BodyCell>{summaryCell(s.userId)}</BodyCell>}
                  <BodyCell class="text-center">
                    {waitingFor(s.userId) ? (
                      <span class="inline-block text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                        {waitingFor(s.userId)}
                      </span>
                    ) : (
                      <span class="text-gray-400">—</span>
                    )}
                  </BodyCell>
                  <BodyCell>
                    <span
                      class={`inline-block text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_CLASSES[status]}`}
                    >
                      {ROSTER_STATUS_LABELS[status]}
                    </span>
                    {trackerLive && (
                      <span class="inline-flex flex-wrap gap-1 ml-1">
                        <TrackerChips student={trackerStudents.get(s.userId)} />
                      </span>
                    )}
                  </BodyCell>
                  {trackerColumns.map((column) => (
                    <BodyCell key={column.key} class="text-center">
                      <TrackerCountCell column={column} student={trackerStudents.get(s.userId)} />
                    </BodyCell>
                  ))}
                </div>
              ))}
            </div>
          </div>
          {trackerLive && participantChannels(trackerLive.config) && (
            <span class="text-sm text-gray-600">
              Large: this week. Grey: last week. Hours are priced at each participant's own rate.
              Hover a cost per lead for their target.
            </span>
          )}
          {trackerColumns.some((c) => c.isCloses) && (
            <span class="text-sm text-gray-600">
              Large: this week. Grey: last week. Convos are conversations held. Closes follow each
              person's own choice: booked or collected.
            </span>
          )}
        </>
      )}

      {view === 'totals' && tracker && <TotalsView courseId={courseId} overview={tracker} />}

      {view === 'grid' && grid.days.length > 0 && (
        <>
          <div class="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-600">
            <span>
              {grid.startDate
                ? `Newest first, back to ${dayjs(grid.days[0]).format('ddd M/D')}.`
                : `The last ${grid.days.length} standup days, newest first.`}
            </span>
            <span class="flex items-center gap-1.5">
              <IcoCheck class="w-4 h-4 text-green-600" /> Answered
            </span>
            <span class="flex items-center gap-1.5">
              <IcoX class="w-4 h-4 text-red-500" /> Missed
            </span>
            <span class="flex items-center gap-1.5">
              <span class="text-gray-300">—</span> Not due
            </span>
          </div>
          <div class="overflow-x-auto">
            <div class="table bg-white rounded-sm border w-full divide-y">
              <div class="table-row bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <HeadCell class="w-64 sticky left-0 bg-gray-50">Student</HeadCell>
                <HeadCell class="text-center">Answered</HeadCell>
                {gridColumns.map(({ day }) => (
                  <HeadCell key={day} class="text-center whitespace-nowrap">
                    {dayjs(day).format('ddd M/D')}
                  </HeadCell>
                ))}
              </div>
              {rows.map(({ student: s, summary }) => (
                <div key={s.userId} class="table-row text-sm">
                  {studentCell(s)}
                  <BodyCell class="text-center font-semibold text-gray-800 whitespace-nowrap">
                    {summary.answered} of {summary.due}
                  </BodyCell>
                  {gridColumns.map(({ day, i }) => (
                    <BodyCell key={day} class="text-center">
                      {summary.cells[i].state === 'answered' && (
                        <IcoCheck class="w-4 h-4 inline-block text-green-600" />
                      )}
                      {summary.cells[i].state === 'missed' && (
                        <IcoX class="w-4 h-4 inline-block text-red-500" />
                      )}
                      {summary.cells[i].state === 'notDue' && <span class="text-gray-300">—</span>}
                    </BodyCell>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
